import React, { useState } from "react";
import { Modal } from "@/components/Modal";
import { ProductImage } from "@/components/ProductImage";
import { eraLabel, CATEGORY_LABELS } from "@/lib/util";
import { Heart, Volume2, ExternalLink } from "lucide-react";

export const NostalgiaCard = ({ product, open, onClose, unlocked, onAdd }) => {
  const [popped, setPopped] = useState(false);
  const [idx, setIdx] = useState(0);
  React.useEffect(() => setIdx(0), [product?.id]);
  if (!product) return null;

  const gallery = (product.images && product.images.length ? product.images : product.image_url ? [product.image_url] : []);

  const handleAdd = () => {
    onAdd(product.id);
    setPopped(true);
    setTimeout(() => setPopped(false), 500);
  };

  return (
    <Modal open={open} onClose={onClose} testId="nostalgia-card" maxWidth="max-w-md">
      <div className="p-5 sm:p-7">
        {/* image */}
        <div className="relative mb-2 flex h-52 items-center justify-center overflow-hidden rounded-md border-[3px] border-kirana-brown bg-kirana-cream">
          {gallery.length ? (
            <img src={gallery[idx]} alt={product.name} className="h-full w-full object-contain" />
          ) : (
            <ProductImage product={product} big />
          )}
          {product.discontinued && (
            <span className="absolute left-2 top-2 rounded border-2 border-kirana-brown bg-kirana-brown px-2 py-0.5 text-[10px] font-bold uppercase text-kirana-cream">
              Discontinued
            </span>
          )}
        </div>

        {/* gallery thumbnails */}
        {gallery.length > 1 && (
          <div className="mb-3 flex gap-1.5 overflow-x-auto scrollbar-thin pb-1" data-testid="card-gallery">
            {gallery.map((src, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-11 w-11 shrink-0 overflow-hidden rounded border-2 ${
                  i === idx ? "border-kirana-red" : "border-kirana-border"
                }`}
              >
                <img src={src} alt={`${product.name} ${i + 1}`} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}

        {/* name */}
        <h2
          data-testid="nostalgia-card-name"
          className="font-signboard text-3xl font-bold leading-none text-kirana-brown"
        >
          {product.name}
        </h2>

        {/* tagline / memory */}
        {product.tagline ? (
          <p className="mt-2 text-lg italic text-kirana-red">"{product.tagline}"</p>
        ) : product.nostalgia_description ? (
          <p className="mt-2 text-sm italic text-kirana-red">"{product.nostalgia_description}"</p>
        ) : null}

        {/* badges */}
        <div className="mt-3 flex flex-wrap gap-2">
          <Badge>{CATEGORY_LABELS[product.category] || product.category}</Badge>
          <Badge tone="green">{eraLabel(product.era_start)}</Badge>
          {product.approximate_price && (
            <span
              title="Prices varied by year, pack size and region."
              className="inline-flex cursor-help items-center gap-1 rounded border-2 border-kirana-brown bg-kirana-gold px-2 py-0.5 text-xs font-bold text-kirana-brown"
            >
              💰 {product.approximate_price}
              {product.price_pack_size ? ` · ${product.price_pack_size}` : ""}
            </span>
          )}
        </div>

        {product.tagline && product.nostalgia_description && (
          <p className="mt-3 rounded-md border-2 border-dashed border-kirana-border bg-kirana-cream/60 p-3 text-sm text-kirana-brown">
            {product.nostalgia_description}
          </p>
        )}

        {/* jingle info — only shown when a jingle exists */}
        {product.jingle_available && (
          <div className="mt-4 flex items-center gap-2 rounded-md border-2 border-kirana-border bg-kirana-cream/60 p-3 text-sm text-kirana-brown">
            <Volume2 className="h-4 w-4 text-kirana-green" />
            <span className="font-semibold">Jingle:</span>
            <span className="italic">{product.tagline || "Iconic ad jingle (external source)"}</span>
          </div>
        )}

        {/* meta: notes + original source */}
        <div className="mt-4 space-y-1 text-[11px] text-kirana-brown/70">
          {product.notes && <p className="italic">Note: {product.notes}</p>}
          {product.external_source_url && (
            <a
              href={product.external_source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-semibold text-kirana-red underline"
            >
              View Original Source <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>

        {/* memory action */}
        <div className="mt-5 flex items-center justify-center">
          {unlocked ? (
            <div
              data-testid="memory-unlocked"
              className="flex items-center gap-2 rounded-md border-2 border-kirana-red bg-kirana-red/10 px-4 py-2 font-bold text-kirana-red"
            >
              <Heart className={`h-5 w-5 fill-kirana-red ${popped ? "animate-heart-pop" : ""}`} />
              MEMORY UNLOCKED
            </div>
          ) : (
            <button
              data-testid="memory-add-btn"
              onClick={handleAdd}
              className="flex items-center gap-2 rounded-md border-[3px] border-kirana-brown bg-kirana-red px-5 py-2.5 font-bold text-white transition-transform hover:scale-[1.03] hard-shadow"
            >
              <Heart className="h-5 w-5" />
              Add to Memories
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

const Badge = ({ children, tone }) => (
  <span
    className={`rounded border-2 border-kirana-brown px-2 py-0.5 text-xs font-bold ${
      tone === "green" ? "bg-kirana-green text-white" : "bg-kirana-cream text-kirana-brown"
    }`}
  >
    {children}
  </span>
);
